import userService, {
  UserListParams,
  AdminUser,
  UserListResponse,
  ChangeMyPasswordRequest,
  SetUserPasswordRequest,
} from "@/services/userService";
import Roles from "@/constants/Roles";

type State = {
  users: AdminUser[];
  currentUser: AdminUser | null;
  total: number;
  loading: boolean;
};

const state: State = {
  users: [],
  currentUser: null,
  total: 0,
  loading: false,
};

const mutations = {
  setUsers(state: State, users: AdminUser[]) {
    state.users = users;
  },
  setCurrentUser(state: State, user: AdminUser | null) {
    state.currentUser = user;
  },
  setTotal(state: State, total: number) {
    state.total = total;
  },
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
};

const getters = {
  users: (state: State) => state.users,
  currentUser: (state: State) => state.currentUser,
  total: (state: State) => state.total,
  loading: (state: State) => state.loading,
};

const actions = {
  async loadUsers(
    { commit, rootGetters }: { commit: any; rootGetters: any },
    params: UserListParams = {}
  ) {
    try {
      if (!rootGetters["auth/isAuthenticated"]) {
        commit("setUsers", []);
        commit("setTotal", 0);
        return [];
      }
      commit("setLoading", true);
      const response: UserListResponse = await userService.listUsers(params);
      commit("setUsers", response.items);
      commit("setTotal", response.totalItems);
      return response.items;
    } catch (error) {
      commit("setUsers", []);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async loadUserById({ commit }: { commit: any }, userId: string) {
    try {
      commit("setLoading", true);
      const user = await userService.getUserById(userId);
      commit("setCurrentUser", user);
      return user;
    } catch (error) {
      commit("setCurrentUser", null);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async createUser(_: any, userData: any) {
    return userService.createUser(userData);
  },

  async updateUser(_: any, { userId, data }: { userId: string; data: any }) {
    return userService.updateUser(userId, data);
  },

  async updateUserRole(
    _: any,
    { userId, role, dealerId }: { userId: string; role: Roles; dealerId?: string | null }
  ) {
    return userService.updateUserRole(userId, role, dealerId);
  },

  async changeMyPassword(_: any, data: ChangeMyPasswordRequest) {
    return userService.changeMyPassword(data);
  },

  async setUserPassword(
    _: any,
    { userId, data }: { userId: string; data: SetUserPasswordRequest }
  ) {
    return userService.setUserPassword(userId, data);
  },

  async activateUser(_: any, userId: string) {
    return userService.activateUser(userId);
  },

  async deactivateUser(_: any, userId: string) {
    return userService.deactivateUser(userId);
  },

  async deleteUser(_: any, userId: string) {
    return userService.deleteUser(userId);
  },

  clearCurrentUser({ commit }: { commit: any }) {
    commit("setCurrentUser", null);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
